import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LockIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAuthStore } from '../../stores/authStore';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useBrandingStore } from '../../stores/brandingStore';
import { useUIStore } from '../../stores/uiStore';
import { useAuth } from '../../hooks/useAuth';
import { MODULES } from '../../constants/modules';
import { NAV_SECTIONS } from '../../constants/navConfig';
import { ROUTES } from '../../constants/routes';
import { QUERY_KEYS } from '../../constants/queryKeys';
import { getOnboardingStatus } from '../../api/onboarding';
import UpgradePrompt from '../common/UpgradePrompt';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// ─── Setup Guide Progress Ring ─────────────────────────────────────────────────

function ProgressRing({ pct, size = 28, stroke = 3 }: { pct: number; size?: number; stroke?: number }) {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="shrink-0 -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--color-secondary, #64748b)"
        strokeWidth={stroke}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.4s ease' }}
      />
    </svg>
  );
}

function SetupGuideEntry({ collapsed }: { collapsed: boolean }) {
  const { data } = useQuery({
    queryKey: QUERY_KEYS.onboarding.status(),
    queryFn: getOnboardingStatus,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  if (!data || data.onboarding_completed) return null;

  const pct = data.completion_percentage;

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={ROUTES.ONBOARDING}
            className="flex justify-center py-2 px-2 hover:opacity-80 transition-opacity"
            aria-label="Setup Guide"
          >
            <ProgressRing pct={pct} size={28} stroke={3} />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">Setup Guide · {pct}%</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div
      className="border-t px-3 py-3"
      style={{ borderColor: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 10%, transparent)` }}
    >
      <Link
        to={ROUTES.ONBOARDING}
        className="flex items-center gap-2.5 rounded-md px-2 py-2 transition-colors hover:opacity-80"
        style={{ color: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 80%, transparent)` }}
      >
        <ProgressRing pct={pct} size={28} stroke={3} />
        <div className="min-w-0 flex-1">
          <p
            className="text-xs font-medium leading-tight"
            style={{ color: `var(--sidebar-text-color, #ffffff)` }}
          >
            Setup Guide
          </p>
          <p
            className="text-[10px] leading-tight"
            style={{ color: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 55%, transparent)` }}
          >
            {pct}% complete
          </p>
        </div>
      </Link>
    </div>
  );
}

export default function Sidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { can, role } = useAuth();
  const company = useAuthStore((s) => s.company);
  const logoUrl = useBrandingStore((s) => s.config?.logo_url);

  const hasModule = useSubscriptionStore((s) => s.hasModule);
  const isTrial = useSubscriptionStore((s) => s.isTrial);
  const tier = useSubscriptionStore((s) => s.tier);
  const trialEndsAt = useSubscriptionStore((s) => s.trial_ends_at);
  const freeCallsRemaining = useSubscriptionStore((s) => s.ai_copilot_free_calls_remaining);
  const hasCopilotAddon = useSubscriptionStore((s) => s.hasCopilotAddon);

  const TIER_LABELS: Record<string, string> = {
    operate: 'Operate',
    scale: 'Scale',
    enterprise: 'Enterprise',
    group: 'Group',
    starter: 'Operate', // map legacy "starter" → "Operate"
  };

  const { sidebarCollapsed, toggleSidebar } = useUIStore();

  const [upgradeModuleId, setUpgradeModuleId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const handler = () => setNow(Date.now());
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, []);

  const trialDaysLeft = useMemo(() => {
    if (!trialEndsAt) return 0;
    const trialEnd = new Date(trialEndsAt).getTime();
    const diff = trialEnd - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [trialEndsAt, now]);

  // Initials for collapsed logo fallback
  const initials = company?.name
    ? company.name
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
    : 'FP';

  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex h-full flex-col bg-sidebar overflow-hidden" style={{ color: 'var(--sidebar-text-color, #ffffff)' }}>
        {/* Logo / brand */}
        <div
          className="flex h-16 items-center border-b px-3"
          style={{ borderColor: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 10%, transparent)` }}
        >
          <Link
            to={ROUTES.DASHBOARD}
            className={cn('flex items-center gap-3 min-w-0 flex-1 cursor-pointer hover:opacity-80 transition-opacity', sidebarCollapsed && 'justify-center')}
          >
            {sidebarCollapsed ? (
              logoUrl ? (
                <img src={logoUrl} alt="Company logo" className="h-6 w-6 object-contain shrink-0" />
              ) : (
                <span className="text-sm font-bold tracking-tight shrink-0">{initials}</span>
              )
            ) : logoUrl ? (
              <img src={logoUrl} alt="Company logo" className="h-8 w-auto object-contain shrink-0" />
            ) : (
              <span className="text-base font-bold tracking-tight shrink-0">Fast Policy</span>
            )}
            {!sidebarCollapsed && company?.name && (
              <span
                className="truncate text-xs mt-0.5"
                style={{ color: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 60%, transparent)` }}
              >
                {company.name}
              </span>
            )}
          </Link>
          <button
            type="button"
            onClick={toggleSidebar}
            className="shrink-0 rounded-md p-1 transition-colors"
            style={{
              color: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 50%, transparent)`,
              ['--tw-bg-opacity' as string]: '1',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--sidebar-overlay-color, rgba(255,255,255,0.10))'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--sidebar-text-color, #ffffff)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = ''; (e.currentTarget as HTMLButtonElement).style.color = `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 50%, transparent)`; }}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Nav sections */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {NAV_SECTIONS.map(({ section, items }) => {
            const renderedItems = items.filter((item) => {
              if (!role) return false;
              if (item.permission !== null && !can(item.permission)) return false;
              if (item.brandingOnly && !company?.is_white_label) return false;
              return true;
            });

            if (renderedItems.length === 0) return null;

            return (
              <div key={section} className="mb-4">
                {!sidebarCollapsed && (
                  <p
                    className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest"
                    style={{ color: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 40%, transparent)` }}
                  >
                    {section}
                  </p>
                )}

                {renderedItems.map((item) => {
                  const isLocked =
                    item.moduleId !== null &&
                    item.moduleId !== MODULES.AI_COPILOT &&
                    item.moduleId !== MODULES.FAST_INSPECTIONS &&
                    !hasModule(item.moduleId);
                  const isActive = pathname.startsWith(item.href);
                  const isAiCopilot = item.moduleId === MODULES.AI_COPILOT;

                  if (isLocked) {
                    return (
                      <Tooltip key={item.href}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => setUpgradeModuleId(item.moduleId)}
                            className={cn(
                              'flex w-full items-center rounded-md px-3 py-2 text-sm',
                              'opacity-50 cursor-not-allowed',
                              sidebarCollapsed ? 'justify-center gap-0' : 'gap-3',
                            )}
                          >
                            <item.icon className="h-4 w-4 shrink-0" />
                            {!sidebarCollapsed && (
                              <>
                                <span className="flex-1 text-left truncate">{item.label}</span>
                                <LockIcon className="h-3 w-3 shrink-0" />
                              </>
                            )}
                          </button>
                        </TooltipTrigger>
                        {sidebarCollapsed && (
                          <TooltipContent side="right">{item.label}</TooltipContent>
                        )}
                      </Tooltip>
                    );
                  }

                  return (
                    <Tooltip key={item.href}>
                      <TooltipTrigger asChild>
                        <Link
                          to={item.href}
                          onClick={() => navigate(item.href)}
                          className={cn(
                            'flex items-center rounded-md px-3 py-2 text-sm transition-colors',
                            sidebarCollapsed ? 'justify-center gap-0' : 'gap-3',
                            isActive ? 'font-medium border-l-2' : '',
                          )}
                          style={{
                            color: isActive
                              ? `var(--sidebar-text-color, #ffffff)`
                              : `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 70%, transparent)`,
                            backgroundColor: isActive ? `rgba(128,128,128,0.15)` : undefined,
                            borderLeftColor: isActive ? `var(--color-secondary, #64748b)` : undefined,
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'var(--sidebar-overlay-color, rgba(255,255,255,0.10))';
                              (e.currentTarget as HTMLAnchorElement).style.color = `var(--sidebar-text-color, #ffffff)`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '';
                              (e.currentTarget as HTMLAnchorElement).style.color = `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 70%, transparent)`;
                            }
                          }}
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          {!sidebarCollapsed && (
                            <>
                              <span className="flex-1 truncate">{item.label}</span>
                              {isAiCopilot && (
                                hasCopilotAddon() ? (
                                  <span className="rounded-full bg-green-500/20 px-1.5 py-0.5 text-[10px] font-medium text-green-300">
                                    Unlimited
                                  </span>
                                ) : freeCallsRemaining !== undefined ? (
                                  <span className="rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
                                    {freeCallsRemaining} free
                                  </span>
                                ) : null
                              )}
                            </>
                          )}
                        </Link>
                      </TooltipTrigger>
                      {sidebarCollapsed && (
                        <TooltipContent side="right">{item.label}</TooltipContent>
                      )}
                    </Tooltip>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Setup guide progress */}
        {!company?.onboarding_completed && role === 'ic-admin' && (
          <SetupGuideEntry collapsed={sidebarCollapsed} />
        )}

        {/* Subscription badge */}
        <div
          className="border-t p-4"
          style={{ borderColor: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 10%, transparent)` }}
        >
          {isTrial() ? (
            sidebarCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-center">
                    <div className="h-2 w-2 rounded-full bg-amber-400" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  Trial — {trialDaysLeft} days remaining
                </TooltipContent>
              </Tooltip>
            ) : (
              <div className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                <span className="font-semibold">Trial</span> — {trialDaysLeft} days remaining
              </div>
            )
          ) : tier ? (
            sidebarCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-center">
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 40%, transparent)` }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {TIER_LABELS[tier!] ?? tier} plan
                </TooltipContent>
              </Tooltip>
            ) : (
              <div
                className="rounded-md px-3 py-2 text-xs"
                style={{
                  backgroundColor: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 5%, transparent)`,
                  color: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 60%, transparent)`,
                }}
              >
                <span
                  className="font-medium"
                  style={{ color: `color-mix(in srgb, var(--sidebar-text-color, #ffffff) 80%, transparent)` }}
                >
                  {TIER_LABELS[tier!] ?? tier}
                </span>{' '}
                plan
              </div>
            )
          ) : null}
        </div>

        {/* Upgrade prompt dialog */}
        {upgradeModuleId && (
          <UpgradePrompt
            moduleId={upgradeModuleId}
            open={!!upgradeModuleId}
            onClose={() => setUpgradeModuleId(null)}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
